import { useEffect, useState } from 'react';
import { API } from '@/utils/API';
import {
  RegistrationFormData,
  organizationTypeEnum,
} from '@/features/auth/registration/schema';

declare global {
  interface Window {
    grecaptcha: {
      reset: () => void;
    };
  }
}

export interface SignUpResult {
  success: boolean;
  error?: string;
}

export type RegistrationStep = 'form' | 'confirm_code' | 'success';

const RESEND_COOLDOWN_SECONDS = 60;

interface BackendRegistrationPayload {
  registration: {
    email: string;
    password: string;
    family_name: string;
    given_name: string;
    middle_name?: string;
    phone_number: string;
    country_of_residence?: string;
    country_of_business?: string;
    organization_name?: string;
    organization_type?: (typeof organizationTypeEnum)[number];
    protocol_number?: string;
    recaptcha_token: string;
    invitation_token?: string;
    terms_version: string;
    terms_accepted_at: string;
  };
}

export const useRegistration = ({
  invitationToken,
  initialEmail,
}: {
  invitationToken?: string | null;
  // When provided, the hook starts on the confirm-code step for this email.
  // Used by the standalone /confirm-registration page so a registrant who
  // left the signup flow (tab close, refresh, legacy migrated row) can finish
  // verifying without re-registering.
  initialEmail?: string | null;
} = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<RegistrationStep>(
    initialEmail ? 'confirm_code' : 'form'
  );
  const [submittedEmail, setSubmittedEmail] = useState(initialEmail ?? '');
  const [resendCooldown, setResendCooldown] = useState(0);
  // True when the backend approved the account on confirmation (registrant
  // was invited by a Submission/Site/Study Manager) — they can sign in now.
  const [approved, setApproved] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(
      () => setResendCooldown(seconds => seconds - 1),
      1000
    );
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const signUp = async (data: RegistrationFormData): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await registerUserInBackend(data, invitationToken);

      setSubmittedEmail(data.email);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setStep('confirm_code');
    } catch (err) {
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
      setError(getFriendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // emailOverride lets the standalone confirmation page pass a user-typed
  // email; the in-flow signup path relies on the remembered submittedEmail.
  const confirmCode = async (
    code: string,
    emailOverride?: string
  ): Promise<void> => {
    const email = emailOverride ?? submittedEmail;
    setIsLoading(true);
    setError(null);

    try {
      const { approved: isApproved } = await confirmEmailInBackend(email, code);
      setApproved(isApproved);
      setSubmittedEmail(email);
      setStep('success');
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async (emailOverride?: string): Promise<void> => {
    if (resendCooldown > 0) return;

    const email = emailOverride ?? submittedEmail;
    if (!email) return;

    setError(null);

    try {
      const res = await API.fetch('/auth/resend_confirmation_code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      // fetch only rejects on network errors, so a throttled 429 resolves
      // normally — check res.ok before restarting the cooldown, otherwise the
      // UI implies a code was sent when none was.
      if (!res.ok) {
        setError(
          await messageFromResponse(
            res,
            'Could not resend the code. Please try again shortly.'
          )
        );
        return;
      }

      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getFriendlyError(err));
    }
  };

  return {
    signUp,
    confirmCode,
    resendCode,
    step,
    submittedEmail,
    approved,
    resendCooldown,
    isLoading,
    error,
    clearError: () => setError(null),
  };
};

// private functions

const getFriendlyError = (error: unknown): string =>
  error instanceof Error
    ? {
        UsernameExistsException:
          "We couldn't complete the request. If the account exists, check your inbox for next steps.",
        InvalidPasswordException: 'Password does not meet requirements.',
        InvalidParameterException:
          'Invalid information provided. Please check your details.',
      }[error.name] ||
      error.message ||
      'Unexpected error occurred.'
    : 'Unexpected error occurred.';

// Extracts a human-readable message from an error response. Handles both the
// service-failure envelope ({ errors }) and the bad-request envelope
// ({ error }), each of which may be a string, array, or nothing, and a
// non-JSON body (e.g. a rack-attack 429). Never surfaces a raw JSON parse
// error or "[object Object]".
const messageFromResponse = async (
  res: Response,
  fallback: string
): Promise<string> => {
  const body = await res.json().catch(() => null);
  const raw = body?.errors ?? body?.error;

  if (Array.isArray(raw)) return raw.join(', ') || fallback;
  if (typeof raw === 'string') return raw || fallback;
  return fallback;
};

const registerUserInBackend = async (
  data: RegistrationFormData,
  invitationToken?: string | null
): Promise<SignUpResult> => {
  const backendData = createBackendPayload(data, invitationToken);

  const res = await API.fetch('/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(backendData),
  });

  if (res.ok) {
    return { success: true };
  }

  throw new Error(
    await messageFromResponse(res, 'Failed to create user registration record')
  );
};

const confirmEmailInBackend = async (
  email: string,
  code: string
): Promise<{ approved: boolean }> => {
  const res = await API.fetch('/auth/confirm_registration', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });

  if (res.ok) {
    const body: unknown = await res.json().catch(() => null);
    return { approved: isApprovedResponse(body) };
  }

  throw new Error(await messageFromResponse(res, 'Failed to verify email'));
};

const isApprovedResponse = (body: unknown): boolean =>
  typeof body === 'object' &&
  body !== null &&
  'approved' in body &&
  body.approved === true;

const createBackendPayload = (
  data: RegistrationFormData,
  invitationToken?: string | null
): BackendRegistrationPayload => {
  return {
    registration: {
      email: data.email,
      password: data.password,
      family_name: data.lastName,
      given_name: data.firstName,
      middle_name: data.middleInitial,
      phone_number: data.mobilePhone,
      country_of_residence: data.countryOfResidence,
      country_of_business: data.countryOfBusiness,
      organization_name: data.organizationName,
      organization_type: data.organizationType,
      recaptcha_token: data.recaptchaToken,
      invitation_token: invitationToken || undefined,
      terms_version: data.termsAndCondition.version || '',
      terms_accepted_at: data.termsAndCondition.acceptedAt || '',
    },
  };
};
